import { useTranslation } from "react-i18next";
import ChartEvent from "./ChartEvent";
import ChartTemp from "./ChartTemp";
import LyingPosition from "./LyingPosition";
import "./styles.scss";
import SelectComponent from "components/select/Select";
import { useRef, useState } from "react";
import * as htmlToImage from 'html-to-image';
import { jsPDF } from 'jspdf';
import ButtonComponent from "components/button/Button";
import moment from "moment";
import Loading from "components/loading/Loading";
import dayjs from "dayjs";
import { apiExportRecordsCsvAuto, apiExportRecordsCsvManual } from "helpers/api/records";

const ChartDay = ({ dataSleep, dataTemperature, dataEvent, patientId, startDate, dateSelected }) => {
  const { t } = useTranslation()
  const [exportType, setExportType] = useState(0);
  const printRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const startTime = Date.UTC(
    new Date(startDate).getFullYear(),
    new Date(startDate).getMonth(),
    new Date(startDate).getDate() - 1,
    17,
    30
  );

  const endTime = Date.UTC(
    new Date(startDate).getFullYear(),
    new Date(startDate).getMonth(),
    new Date(startDate).getDate(),
    9,
    30
  );

  const EXPORT_OPTIONS_VALUE = {
    PDF: 0,
    CSV_MANUAL: 1,
    CSV_AUTO: 2,
  };

  const exportOption =
    [
      {
        label: t("graph.temperaturePosition"),
        value: EXPORT_OPTIONS_VALUE.PDF,
        prefix: "/icons/pdf.webp",
      }
    ];

  const handleExport = async () => {
    setIsLoading(true);
    switch (exportType) {
      case EXPORT_OPTIONS_VALUE.PDF:
        exportPDF();
        break;
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        exportCSV(exportType);
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        exportCSV(exportType);
        break;
      default:
        setIsLoading(false);
        break;
    }
  };

  const exportPDF = async () => {
    if (printRef.current) {
      const element = printRef.current;
      const images1 = element.querySelectorAll('svg image');
      images1.forEach((img) => {
        img.outerHTML = img.outerHTML.replace("xlink:", "")
      });
      const elementToExport = element.cloneNode(true);
      const images = elementToExport.querySelectorAll('svg image');
      images.forEach((img) => {
        img.outerHTML = img.outerHTML.replace("xlink:", "")
      });
      elementToExport.style.padding = "30px 20px";
      elementToExport.style.paddingBottom = "300px";
      elementToExport.style.backgroundImage = 'url(/background_urine.png)';

      // Temp encluding element to export.
      const temp = document.createElement('div');
      temp.style.position = 'absolute';
      temp.style.left = '-9999px';

      // Title in the PDF file.
      const title = document.createElement('h1');
      title.textContent = t("graph.exportPDF.title");
      title.style.fontWeight = "600";
      title.style.fontSize = "1.8em";
      title.style.marginBottom = "0";

      // Time period.
      const timeString = document.createElement('span');
      timeString.textContent = `${t("graph.exportPDF.timeStr")}${moment(startDate).format("YYYY年M月D日分")}`;
      timeString.style.fontSize = "1.2em";

      // Copyright.
      const copyright = document.createElement('span');
      copyright.textContent = t("graph.exportPDF.copyright");
      copyright.style.display = "block";
      copyright.style.textAlign = "right";
      copyright.style.fontSize = "0.6em";

      const PDFHeader = document.createElement('div');
      PDFHeader.style.textAlign = "center";
      PDFHeader.style.padding = '10px';
      PDFHeader.style.margin = '5px 0';
      PDFHeader.appendChild(title);
      PDFHeader.appendChild(timeString);

      elementToExport.style.padding = "10px";
      elementToExport.insertBefore(PDFHeader, elementToExport.firstChild);
      elementToExport.appendChild(copyright);

      // Custom style for chart
      const charts = elementToExport.getElementsByClassName('highcharts-background');
      const plotCharts = elementToExport.getElementsByClassName('highcharts-plot-background');
      for (const el of charts) {
        el.style.fill = "none";
      }

      for (const el of plotCharts) {
        el.style.fill = "#ffffff";
      }

      temp.appendChild(elementToExport);
      document.body.appendChild(temp);

      const imgData = await htmlToImage.toJpeg(elementToExport);
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
      });

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${t("graph.exportPDF.fileNameDayChart")}.pdf`);

      // clean up the temp
      document.body.removeChild(temp);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }

  const exportCSV = async (type) => {
    const from = dayjs(dateSelected)
      .startOf("month")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");
    const to = dayjs(dateSelected)
      .endOf("month")
      .endOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");

    let res = {};

    switch (type) {
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        res = await apiExportRecordsCsvManual({ from, to, patient_id: patientId });
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        res = await apiExportRecordsCsvAuto({ from, to, patient_id: patientId });
        break;
      default:
        break;
    }

    if (res.data) {
      window.location.href = res.data;
    }
    setIsLoading(false);
    return false;
  }

  return (
    <div>
      <div className="chart-day-container" ref={printRef}>
        <LyingPosition data={dataSleep} startTime={startTime} endTime={endTime} />
        <ChartTemp data={dataTemperature} startTime={startTime} endTime={endTime} />
        <ChartEvent data={dataEvent} startTime={startTime} endTime={endTime} />
        <div className="chart-day-legend">
          <div>{t('graph.tempePosi.faceUp')}</div>
          <div>{t('graph.tempePosi.faceDown')}</div>
        </div>
      </div>
      <div className="export-section">
        <SelectComponent
          options={exportOption}
          popupClassName="export-section-options"
          onChange={(e) => setExportType(e)}
          defaultValue={EXPORT_OPTIONS_VALUE.PDF}
          optionRender={(option) => {
            return (
              <div
                style={{
                  alignItems: "center",
                  fontWeight: "bold",
                }}
              >
                <img
                  src={option?.data?.prefix}
                  width="30px"
                  style={{ marginRight: "10px" }}
                />
                {option.label}
              </div>
            );
          }}
        />
        <ButtonComponent
          background="#035697"
          className="export-button"
          text={t("graph.calendarPage.export")}
          type="button"
          onClick={handleExport}
        />
      </div>
      {isLoading && <Loading />}
    </div>

  );
};

export default ChartDay;
